import React from "react";

import { css } from "@emotion/react";

import { headlineStyles } from "../styles";
import { PlpSparePartFinderShowSpareParts } from "@swo/storefront";
import { useAsyncValue } from "../../../hooks";
import { SparePartsLoadingScreen } from "./spare-parts-loading-screen";
import { PlpSitemapLinker } from "../sitemap/plp-sitemap";
import { SparePartsCard } from "../shared/spare-parts-card";
import { formatStartingPrice } from "../../../domain/swarovski-optik/products/price/format-price";
import { usePageContext } from "../../../global/page-context";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { SparePartsFinderBottomNavigation } from "../shared/spare-parts-finder-bottom-navigation";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { CSS_MQ } from "../../../styles/breakpoints";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import { fontColor, FONTS, fontSize, fontStyles } from "../../../styles/fonts";
import { LeftHandSideImage } from "../shared/left-hand-side-image";
import { SparePartsProductListingPageTranslation } from "@swo/storefront";
import { T } from "../../../components/T";

export const ShowSparePartsScreenLevel: React.FC<{
  pathname: string;
  fetcher: ({ productKey }: { productKey: string }) => Promise<PlpSparePartFinderShowSpareParts>;
  sitemapLinker: PlpSitemapLinker;
  translations: SparePartsProductListingPageTranslation;
  data: { productKey: string; subfamilyKey: string; categoryKey: string };
}> = props => {
  const {
    sitemapLinker,
    data: { productKey, subfamilyKey, categoryKey },
    translations: t,
  } = props;
  const {
    generalTranslations,
    pageBase: { locale },
  } = usePageContext();

  return useAsyncValue(() => props.fetcher({ productKey }), { dataKey: productKey }).fold({
    onLoading: () => SparePartsLoadingScreen,
    onError: err => <span>Error</span>,
    onLoaded: ({ selectedProduct, selectedSubfamily, selectedCategory, selectedFamily, spareParts }) => {
      const showSpareParts = true;
      const imageUrl = selectedProduct.image?.url;
      const backLink = sitemapLinker("subfamily", { subfamilyKey, categoryKey });
      const subheadline = t.sparePartsFinder;

      return (
        <>
          <div
            css={css`
              width: 100%;
              background-color: ${CSS_COLORS.GreenWhite};

              ${CSS_MQ.atLeastMd} {
                min-height: 320px;
              }

              margin-bottom: ${CSS_SPACER_SIZES.md};
            `}
          >
            <div
              css={css`
                width: var(--max-narrow-content-width);
                margin-left: auto;
                margin-right: auto;
              `}
            >
              <div
                css={css`
                  padding-top: 20px;
                  padding-bottom: 20px;

                  ${CSS_MQ.atLeastMd} {
                    padding-top: 40px;
                    padding-bottom: 40px;

                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 10px;
                  }
                `}
              >
                <LeftHandSideImage
                  image={selectedProduct.image}
                  attachStyles={css`
                    ${CSS_MQ.atMostSm} {
                      width: 100%;
                      max-height: 160px;
                    }
                  `}
                />

                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;

                    ${CSS_MQ.atMostSm} {
                      align-items: center;
                    }
                  `}
                >
                  <span
                    css={[
                      fontStyles.headline5,
                      css`
                        display: block;
                        margin-bottom: 5px;
                      `,
                    ]}
                  >
                    <T>{subheadline}</T>
                  </span>
                  <span
                    css={[
                      headlineStyles,
                      css`
                        text-transform: uppercase;
                      `,
                    ]}
                  >
                    {selectedProduct.name}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ContentContainer>
            <div>
              {spareParts.length > 0 ? (
                <ul
                  css={css`
                    display: grid;
                    gap: 15px;

                    ${CSS_MQ.atLeastSm} {
                      grid-template-columns: repeat(2, minmax(0, 1fr));
                      grid-template-rows: auto;
                    }
                  `}
                >
                  {spareParts.map(sparePart => (
                    <SparePartsCard
                      expectSimilarLayout={false}
                      key={sparePart.link}
                      href={{
                        pathname: sparePart.link,
                        search: "",
                      }}
                      image={sparePart.image}
                      headline={sparePart.name}
                      label={
                        sparePart.lowestPrice
                          ? formatStartingPrice(
                              {
                                startingPrice: sparePart.lowestPrice,
                                discountedStartingPrice: sparePart.discountedLowestPrice,
                                lowestPrice30Days: sparePart.lowestPrice30Days,
                                discountPercentage: sparePart.discountPercentage,
                              },
                              generalTranslations("fromPriceLabel"),
                              generalTranslations("lowestPriceLast30Days"),
                              locale,
                            )
                          : undefined
                      }
                      description={sparePart.description}
                      aspectRatioStyles={css`
                        --aspect-ratio: ${9 / 22};
                      `}
                    />
                  ))}
                </ul>
              ) : (
                <p
                  css={[
                    FONTS.SwarovskiFutura300,
                    fontColor.black,
                    fontSize.x2,
                    css`
                      text-align: center;
                    `,
                  ]}
                >
                  <T>{t.thereAreNoSparePartsForYourProduct}</T>
                </p>
              )}
            </div>

            <Spacer size={"lg"} />
            <SparePartsFinderBottomNavigation link={backLink} centered={true} translations={t} />
          </ContentContainer>
        </>
      );
    },
  });
};
